import React, { useState, useRef, useEffect } from 'react';
import { MapPin, Plus } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useCommunes } from '../../hooks/useCommunes';
import { Commune } from '../../api/communes';

interface CommuneSelectorProps {
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  wilayaCode?: string; // Code de la wilaya pour filtrer les communes
  placeholder?: string;
  className?: string;
  allowCustom?: boolean; // Permettre la saisie manuelle
}

export const CommuneSelector: React.FC<CommuneSelectorProps> = ({
  name,
  value,
  onChange,
  wilayaCode,
  placeholder = "Sélectionner une commune",
  className = "",
  allowCustom = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCommunes, setFilteredCommunes] = useState<Commune[]>([]);
  const [showCustomOption, setShowCustomOption] = useState(false);
  const [showWilayaWarning, setShowWilayaWarning] = useState(false);
  const [lastSelectedValue, setLastSelectedValue] = useState(''); // Dernière valeur sélectionnée
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { communes, isLoading, searchCommunes, loadCommunesByWilaya } = useCommunes();

  useClickOutside(dropdownRef, () => setIsOpen(false), [inputRef.current]);

  // Charger les communes quand la wilaya change
  useEffect(() => {
    if (wilayaCode) {
      console.log('🏛️ CommuneSelector - Wilaya sélectionnée:', wilayaCode);
      loadCommunesByWilaya(wilayaCode);
    }
  }, [wilayaCode, loadCommunesByWilaya]);

  // Filtrer les communes selon la recherche
  useEffect(() => {
    // Si la valeur actuelle est la même que la dernière sélection, ne rien faire
    if (searchTerm === lastSelectedValue) {
      return;
    }
    
    if (searchTerm.trim().length >= 2) {
      if (wilayaCode) {
        // Filtrer dans les communes de la wilaya sélectionnée
        const filtered = communes.filter(commune =>
          commune.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredCommunes(filtered.slice(0, 10));
        
        // Afficher l'option "Ajouter manuellement" si aucun résultat et allowCustom
        setShowCustomOption(allowCustom && filtered.length === 0);
      } else {
        // Recherche globale si pas de wilaya sélectionnée
        searchCommunes(searchTerm, 10).then(results => {
          setFilteredCommunes(results);
          setShowCustomOption(allowCustom && results.length === 0);
        });
      }
      setIsOpen(true);
    } else {
      setFilteredCommunes([]);
      setShowCustomOption(false);
      setIsOpen(false);
    }
  }, [searchTerm, communes, wilayaCode, allowCustom, searchCommunes, lastSelectedValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Vérifier si l'utilisateur essaie de saisir sans avoir sélectionné une wilaya
    if (inputValue.length > 0 && !wilayaCode) {
      setShowWilayaWarning(true);
      return; // Empêcher la saisie
    } else {
      setShowWilayaWarning(false);
    }
    
    setSearchTerm(inputValue);

    // Simuler un événement pour le parent
    onChange({
      target: {
        name,
        value: inputValue,
        type: 'text'
      }
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleCommuneSelect = (commune: Commune) => {
    const selectedValue = commune.name;
    
    setSearchTerm(selectedValue);
    setLastSelectedValue(selectedValue); // Mémoriser la valeur sélectionnée
    
    setIsOpen(false);
    setFilteredCommunes([]);
    setShowCustomOption(false);
    

    // Simuler un événement pour le parent
    onChange({
      target: {
        name,
        value: selectedValue,
        type: 'text'
      }
    } as React.ChangeEvent<HTMLInputElement>);
    
  };

  const handleCustomAdd = () => {
    
    setLastSelectedValue(searchTerm); // Mémoriser la valeur saisie manuellement
    
    setIsOpen(false);
    setFilteredCommunes([]);
    setShowCustomOption(false);
    
    // Garder la valeur saisie manuellement
    onChange({
      target: {
        name,
        value: searchTerm,
        type: 'text'
      }
    } as React.ChangeEvent<HTMLInputElement>);
    
  };

  const handleInputFocus = () => {
    // Vérifier si une wilaya est sélectionnée au focus
    if (!wilayaCode) {
      setShowWilayaWarning(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setFilteredCommunes([]);
      setShowCustomOption(false);
    } else if (e.key === 'ArrowDown' && filteredCommunes.length > 0) {
      e.preventDefault();
      const firstOption = dropdownRef.current?.querySelector('button');
      firstOption?.focus();
    }
  };

  // Synchroniser searchTerm avec value du parent et mémoriser les sélections
  useEffect(() => {
    if (value && !searchTerm) {
      setSearchTerm(value);
      setLastSelectedValue(value); // Mémoriser aussi comme dernière sélection
    }
    
    // Masquer le warning wilaya quand une wilaya est sélectionnée
    if (wilayaCode && showWilayaWarning) {
      setShowWilayaWarning(false);
    }
  }, [value, wilayaCode]);

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          name={name}
          value={searchTerm || value}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={wilayaCode ? `${placeholder} (${communes.length} communes disponibles)` : placeholder}
          className={cn(
            "block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm",
            "pl-10 touch-manipulation",
            className
          )}
          autoComplete="off"
        />
        <MapPin className="absolute left-3 top-1/2 h-5 w-5 sm:h-4 sm:w-4 -translate-y-1/2 text-gray-400" />
        
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-500 border-t-transparent"></div>
          </div>
        )}
      </div>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-800 scrollbar-hide"
        >
          {filteredCommunes.length > 0 ? (
            filteredCommunes.map((commune) => (
              <button
                key={commune.id}
                type="button"
                onClick={() => handleCommuneSelect(commune)}
                className="flex w-full items-center gap-3 px-4 py-3 sm:py-2 text-left text-base sm:text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none dark:hover:bg-gray-700 dark:focus:bg-gray-700 touch-manipulation"
              >
                <span className="font-mono text-sm sm:text-xs text-gray-500 dark:text-gray-400">
                  {commune.wilaya_code}
                </span>
                <span className="text-gray-900 dark:text-white text-base sm:text-sm">
                  {commune.name}
                </span>
                <span className="text-sm sm:text-xs text-gray-500 dark:text-gray-400">
                  ({commune.wilaya_name})
                </span>
              </button>
            ))
          ) : searchTerm.trim().length >= 2 ? (
            <div className="px-4 py-3 sm:py-2 text-base sm:text-sm text-gray-500 dark:text-gray-400">
              {isLoading ? 'Recherche en cours...' : 'Aucune commune trouvée'}
            </div>
          ) : null}

          {/* Option pour ajouter manuellement */}
          {showCustomOption && allowCustom && searchTerm.trim().length >= 2 && (
            <button
              type="button"
              onClick={handleCustomAdd}
              className="flex w-full items-center gap-3 border-t border-gray-200 px-4 py-3 sm:py-2 text-left text-base sm:text-sm hover:bg-primary-50 focus:bg-primary-50 focus:outline-none dark:border-gray-600 dark:hover:bg-primary-900/20 dark:focus:bg-primary-900/20 touch-manipulation"
            >
              <Plus className="h-5 w-5 sm:h-4 sm:w-4 text-primary-500" />
              <span className="text-primary-600 dark:text-primary-400 text-base sm:text-sm">
                Ajouter "{searchTerm}" manuellement
              </span>
            </button>
          )}

          {/* Message d'aide */}
          {!wilayaCode && searchTerm.length < 2 && (
            <div className="px-4 py-3 text-center text-base sm:text-sm text-gray-500 dark:text-gray-400">
              <p>Tapez au moins 2 caractères pour rechercher</p>
              {allowCustom && (
                <p className="mt-1 text-sm sm:text-xs">
                  Si votre commune n'apparaît pas, vous pouvez la saisir manuellement
                </p>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Message d'avertissement wilaya obligatoire */}
      {showWilayaWarning && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-red-200 bg-red-50 p-3 shadow-lg dark:border-red-800 dark:bg-red-900/20">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded-full bg-red-500 flex items-center justify-center">
              <span className="text-xs text-white font-bold">!</span>
            </div>
            <p className="text-sm text-red-700 dark:text-red-300 font-medium">
              Veuillez d'abord sélectionner une wilaya
            </p>
          </div>
          <p className="text-xs text-red-600 dark:text-red-400 mt-1 ml-6">
            La sélection de la wilaya est nécessaire pour charger les villes correspondantes
          </p>
        </div>
      )}
    </div>
  );
};